import type { Direction, Puzzle, Position, WallSpec } from './types.js';

// ─────────────────────────────────────────────
//  Wall storage
//
//  We use a Set<string> with the key format
//  `${x},${y},${side}` for O(1) lookup.
//  The board always stores BOTH sides of a wall
//  (e.g. a South wall on (3,4) is also stored as
//  a North wall on (3,5)) so callers just ask
//  hasWall(x, y, 'S') without caring about the
//  canonical form.
// ─────────────────────────────────────────────

export type WallSet = Set<string>;

function wallKey(x: number, y: number, side: Direction): string {
  return `${x},${y},${side}`;
}

export function buildWallSet(walls: WallSpec[], gridSize: number): WallSet {
  const set: WallSet = new Set();

  const add = (x: number, y: number, side: Direction) => {
    set.add(wallKey(x, y, side));
  };

  // Add border walls automatically
  for (let i = 0; i < gridSize; i++) {
    add(i, 0, 'N');
    add(i, gridSize - 1, 'S');
    add(0, i, 'W');
    add(gridSize - 1, i, 'E');
  }

  // Add puzzle walls (normalized to both sides)
  for (const w of walls) {
    add(w.x, w.y, w.side);
    // Mirror on neighbor
    const { nx, ny, opposite } = neighbor(w.x, w.y, w.side);
    if (inBounds(nx, ny, gridSize)) {
      add(nx, ny, opposite);
    }
  }

  return set;
}

export function hasWall(walls: WallSet, x: number, y: number, side: Direction): boolean {
  return walls.has(wallKey(x, y, side));
}

// ─────────────────────────────────────────────
//  Geometry helpers
// ─────────────────────────────────────────────

const DELTAS: Record<Direction, Position> = {
  N: { x: 0,  y: -1 },
  S: { x: 0,  y:  1 },
  E: { x: 1,  y:  0 },
  W: { x: -1, y:  0 },
};

const OPPOSITE: Record<Direction, Direction> = {
  N: 'S', S: 'N', E: 'W', W: 'E',
};

export function step(pos: Position, dir: Direction): Position {
  const d = DELTAS[dir];
  return { x: pos.x + d.x, y: pos.y + d.y };
}

export function inBounds(x: number, y: number, gridSize: number): boolean {
  return x >= 0 && y >= 0 && x < gridSize && y < gridSize;
}

function neighbor(x: number, y: number, side: Direction) {
  const d = DELTAS[side];
  return { nx: x + d.x, ny: y + d.y, opposite: OPPOSITE[side] };
}

export function posEqual(a: Position, b: Position): boolean {
  return a.x === b.x && a.y === b.y;
}

// ─────────────────────────────────────────────
//  Board context (everything needed to simulate)
// ─────────────────────────────────────────────

export interface BoardContext {
  gridSize: number;
  walls: WallSet;
}

export function createBoardContext(puzzle: Puzzle): BoardContext {
  return {
    gridSize: puzzle.gridSize,
    walls: buildWallSet(puzzle.walls, puzzle.gridSize),
  };
}
